import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import '../App.css';
import { cancelGearOrder, fetchGearOrder } from '../util/apiUtils';
import {
	PITLSummaryActionButton,
	CancelOrderAction,
	DownloadReceiptAction,
	ScheduleConsultAction,
	TrackShipmentAction
} from '../components/ActionButtons';
import Modal from '../components/Modal';
import { OrderedProducts } from './summary/OrderedProducts';
import { canCancelTest } from './summary/CancelTest';
import PITLTransferButton from './patient-transfer/PITLTransferButton';
import PITLTransfer from './patient-transfer/PITLTransfer';
import PITLTransferSuccess from './patient-transfer/PITLTransferSuccess';

/**
 * The Summary page for Physician in the Loop orders
 */
function PITLSummary({ gearOrder, userInfo, setGearOrder, setLoading }) {
	const [transferRequest, setTransferRequest] = useState({});

	if (transferRequest?.action === 'transfer') {
		return (
			<PITLTransfer
				gearOrder={gearOrder}
				transferRequest={transferRequest}
				setTransferRequest={setTransferRequest}
				userInfo={userInfo}
				setGearOrder={setGearOrder}
				setLoading={setLoading}
			/>
		);
	}

	// Scroll to the top of page on 'cancel' or 'success' action
	if (transferRequest?.action) {
		window.scrollTo(0, 0);
	}

	if (transferRequest?.action === 'success') {
		return (
			<PITLTransferSuccess
				gearOrder={gearOrder}
				transferRequest={transferRequest}
				setTransferRequest={setTransferRequest}
			/>
		);
	}

	const orderStatus = getOrderStatus(gearOrder);

	return (
		<div className="space-top">
			<Heading status={orderStatus} gearOrder={gearOrder} />
			<ActionItems
				gearOrder={gearOrder}
				orderStatus={orderStatus}
				userInfo={userInfo}
				setGearOrder={setGearOrder}
				setLoading={setLoading}
			/>
			{orderStatus !== Status.Canceled && (
				<>
					<PITLTransferButton gearOrder={gearOrder} setTransferRequest={setTransferRequest} />
					<OrderedProducts
						gearOrder={gearOrder}
						setLoading={setLoading}
						setGearOrder={setGearOrder}
						userInfo={userInfo}
					/>
					<hr style={{ borderTop: '1px solid #929292', margin: '40px 0px 50px 0' }} />
					<Footer />
				</>
			)}
		</div>
	);
}

/**
 * Create the page title based on the order's status
 *
 * @param status The order status
 * @param gearOrderId The gear order
 */
const Heading = ({ status, gearOrder }) => {
	let title;
	let description;

	switch (status) {
		case Status.OrderPlaced:
			title = 'Your order has been placed.';
			description = 'You will receive an email with tracking details when your collection pack has shipped.';
			break;
		case Status.Shipped:
			title = 'Your collection pack has shipped.';
			description = 'Collection packs typically arrive in 2-5 business days.';
			break;
		case Status.SpecimenMissing:
			title = 'Enter your specimen collection details.';
			description = 'We need your specimen collection details to complete testing.';
			break;
		case Status.ActivationComplete:
			title = `Your confirmation code is ${gearOrder.id}.`;
			description = 'Write this number on the Activation label card and package it with your specimens.';
			break;
		case Status.ReceivedByLab:
			title = 'We have received your specimens.';
			description = 'You will receive an email when results are ready.';
			break;
		case Status.ResultsReady:
			title = 'Your results are available.';
			description = 'Thank you for choosing Genova.';
			break;
		case Status.Canceled:
			title = 'Your order has been Canceled.';
			break;
		case Status.DiscardedSpecimens:
			title = 'There was a problem with your specimens.';
			description = 'Our lab was unable to complete your test using the specimens provided.';
			break;
		case Status.RecollectionOrdered:
			title = 'A recollection pack has been ordered.';
			description = 'You will receive an email with tracking details when your collection pack has shipped.';
			break;
		case Status.RecollectionPending:
			title = 'A recollection request is pending.';
			description = 'Please go back to the review page to confirm your recollection.';
			break;
		default:
			// Incomplete
			title = 'Activation is Incomplete.';
			description =
				'Please return to the previous steps to complete any missing information and submit your order.';
			break;
	}

	return (
		<div>
			<h3 className="title">Order #{gearOrder.id}</h3>
			<h1>{title}</h1>
			{description && <p>{description}</p>}
		</div>
	);
};

/**
 * Builds the action item section (action buttons). This section includes all logic needed to handle the actions (like opening an e-req link or tracking a shipment).
 *
 * @param gearOrder The order
 * @param orderStatus The order's status
 * @param userInfo The details of the user
 * @param setGearOrder A function used to pass new order details to a parent component
 * @param setLoading A function from a parent component to toggle a loading overlay and spinner
 */
const ActionItems = ({ gearOrder, orderStatus, userInfo, setGearOrder, setLoading }) => {
	const [cancelOrderFailure, setCancelOrderFailure] = useState({ error: '', message: '' });
	const [cancelModal, setCancelModal] = useState(false);

	/**
	 * Cancels the currently shipped or confirmed order
	 *
	 * The order cancel will first pull the full packing number from orderFormId then will use that to contact the kit-ordering api that handles order
	 * cancellation. Using the possible returns - cancelPass, cancelDateFail, cancelAlreadyRefunded, and cancelFail, the method will filter errors
	 * accordingly.
	 */
	const handleOrderCancel = () => {
		setLoading(true);
		cancelGearOrder(gearOrder.id, gearOrder.patientInfo.dateOfBirth)
			.then((result) => {
				if (result === 'cancelPass') {
					fetchGearOrder(gearOrder.id, gearOrder.patientInfo.dateOfBirth)
						.then((order) => {
							setGearOrder(order);
						})
						.catch((e) => {
							setCancelOrderFailure({
								status: 'error',
								message: 'An unexpected error has occurred. Please call us at 800-522-4762.'
							});
						})
						.finally(() => {
							setLoading(false);
						});
				} else if (result === 'cancelAlreadyRefunded') {
					setCancelOrderFailure({
						status: 'error',
						message: 'This order has already been refunded. Please refresh the page.'
					});
					setLoading(false);
				} else {
					setCancelOrderFailure({
						status: 'error',
						message: 'Order cancellation failed. Please contact support.'
					});
					setLoading(false);
				}
			})
			.catch((e) => {
				setCancelOrderFailure({
					status: 'error',
					message: 'An unexpected error has occurred. Please call us at 800-522-4762.'
				});
				setLoading(false);
			});
	};

	// Build the list of action buttons to display based on the order's status
	const actionButtons = [];
	switch (orderStatus) {
		case Status.OrderPlaced:
			actionButtons.push(<DownloadReceiptAction key="download" gearOrder={gearOrder} userInfo={userInfo} />);
			// Only allow a full order cancel if ALL packs can be canceled (pack hasn't been returned or recollected)
			const allReqsCanBeCanceled = gearOrder.requisitions?.every((gearReq) =>
				canCancelTest({ gearOrder, gearReq })
			);
			if (allReqsCanBeCanceled) {
				actionButtons.push(
					<CancelOrderAction gearOrder={gearOrder} onClick={() => setCancelModal(true)} key="cancel" />
				);
			}
			break;
		case Status.Shipped:
			actionButtons.push(<DownloadReceiptAction key="download" gearOrder={gearOrder} userInfo={userInfo} />);
			actionButtons.push(
				<TrackShipmentAction
					key="track"
					gearOrderId={gearOrder.id}
					trackingInformation={gearOrder.trackingInformation}
				/>
			);
			break;
		case Status.SpecimenMissing:
			actionButtons.push(<DownloadReceiptAction key="download" gearOrder={gearOrder} userInfo={userInfo} />);
			break;
		case Status.ActivationComplete:
			actionButtons.push(<DownloadReceiptAction key="download" gearOrder={gearOrder} userInfo={userInfo} />);
			break;
		case Status.ReceivedByLab:
			actionButtons.push(<DownloadReceiptAction key="download" gearOrder={gearOrder} userInfo={userInfo} />);
			break;
		case Status.ResultsReady:
			actionButtons.push(<DownloadReceiptAction key="download" gearOrder={gearOrder} userInfo={userInfo} />);
			actionButtons.push(<ScheduleConsultAction key="consult" gearOrderId={gearOrder?.id} />);
			break;
		case Status.Canceled:
			// No actions for canceled orders
			break;
		case Status.DiscardedSpecimens:
			// Recollect action moved into OrderedProducts
			break;
		case Status.RecollectionOrdered:
			actionButtons.push(<DownloadReceiptAction key="download" gearOrder={gearOrder} userInfo={userInfo} />);
			break;
		case Status.RecollectionPending:
			actionButtons.push(<ReturnToOrderAction key="return" gearOrder={gearOrder} />);
			break;
		default:
			// Incomplete
			actionButtons.push(<ReturnToOrderAction key="return" gearOrder={gearOrder} />);
			break;
	}

	if (orderStatus === Status.Canceled) {
		return (
			<p>
				Your original payment, minus the $40 non-refundable fee, will be refunded to your credit or debit card.
			</p>
		);
	}

	return (
		<>
			{actionButtons.length > 0 && <div className="btn-toolbar-20">{actionButtons}</div>}

			{cancelOrderFailure.status === 'error' && (
				<div id="collection-error" className="alert alert-danger bold">
					{cancelOrderFailure.message}
				</div>
			)}

			{cancelModal && (
				<Modal
					title="Order Cancellation Confirmation"
					confirmAction="Yes, Cancel My Order"
					cancelAction="No, Keep My Order"
					onSave={handleOrderCancel}
					onCancel={() => setCancelModal(false)}
					modalHidden={() => setCancelModal(false)}
				>
					<div className="row">
						<div className="form-group col-xs-12" style={{ zIndex: '10' }}>
							<span>Are you sure you would like to cancel your order? </span>
							<span>
								If so, please refer to our{' '}
								<a
									href="https://connect.gdx.net/refund-policy"
									target="_blank"
									rel="noopener noreferrer"
									title="Refund Policy"
								>
									refund policy
								</a>{' '}
								for additional information regarding your refund.
							</span>
						</div>
					</div>
				</Modal>
			)}
		</>
	);
};

/**
 * Creates an ActionButton that will go back to the 'confirm' step.
 *
 * @param gearOrder The gear order
 */
const ReturnToOrderAction = ({ gearOrder }) => {
	const history = useHistory();
	const location = useLocation();

	const redirectToConfirm = () => {
		let url = `/order/${gearOrder.id}/confirm`;
		if (location?.search) {
			url += location.search;
		}
		history.push(url);
	};

	return <PITLSummaryActionButton title="Return to Order" onClick={redirectToConfirm} />;
};

const Footer = () => {
	return (
		<>
			<h2>Important</h2>
			<ul>
				<li>
					Some testing requires advanced planning and modifications to your normal routine. Review the{' '}
					<a href="https://www.gdx.net/tests/prep" target="_blank" rel="noopener noreferrer">
						test prep information here <span className="fa fa-external-link"></span>
					</a>
					.
				</li>
				<li>
					Your collection pack includes a unique id number called the Activation number. Retain a copy of this
					number for easy access to your results when available.
				</li>
				<li>
					Label all specimen containers as instructed and return to this page to enter important details about
					your collection.
				</li>
				<li>Ship your specimens to our lab Monday through Friday using the FedEx materials provided.</li>
				<li>You will receive an email when your results are ready.</li>
			</ul>
		</>
	);
};

// All possible status values
export const Status = Object.freeze({
	OrderPlaced: 'OrderPlaced',
	Shipped: 'Shipped',
	SpecimenMissing: 'SpecimenMissing',
	ActivationComplete: 'ActivationComplete',
	ReceivedByLab: 'ReceivedByLab',
	ResultsReady: 'ResultsReady',
	Incomplete: 'Incomplete',
	Canceled: 'Canceled',
	DiscardedSpecimens: 'DiscardedSpecimens',
	RecollectionOrdered: 'RecollectionOrdered',
	RecollectionPending: 'RecollectionPending'
});

/**
 * Determine the order's status based on details of the order
 *
 * @param gearOrder The order for which to determine status
 */
export const getOrderStatus = (gearOrder) => {
	const hasTrackingNumber = gearOrder.trackingInformation?.trackingNumberList?.length;
	const allReqsHaveAccessionNo = gearOrder.requisitions && gearOrder.requisitions?.every((req) => req.accessionNo);
	const allSamplesCollected = gearOrder.requisitions?.every((req) => req.dateLastSampleCollected);
	const hasDiscardedSpecimens = gearOrder.requisitions?.some((req) =>
		req?.accessionDetails?.some((accessionDetail) => accessionDetail?.discardedSpecimen)
	);
	const recollectionPending = gearOrder.pitlRecollectionOrderSummary?.recollectionPending || false;
	const recollectionCount = gearOrder.pitlRecollectionOrderSummary?.gearReqRecollectionMap
		? Object.keys(gearOrder.pitlRecollectionOrderSummary.gearReqRecollectionMap).length
		: 0;
	const allReqsCanceled = gearOrder.requisitions?.every((req) => req.canceled);

	/**
	 * Status table
		ID   Status        Display Status
		=    ==========    ==============
		1    Incomplete    Received
		2    Complete      Report Review
		3    Printed       Printed
		4    Reviewed      Released
		5    PITLReview    Phy. Review
		6    Received      Received
		7    Error         Report Generation
		8    QCReview      QC Review
		9    Shipped       Shipped
	*/
	// Status 3, 4, or 9 are 'Released'
	const allOrdersReleased =
		gearOrder.requisitions &&
		gearOrder.requisitions?.every(
			(req) => req.accessionStatusId === 3 || req.accessionStatusId === 4 || req.accessionStatusId === 9
		);

	// All reqs have been canceled -> Canceled
	if (allReqsCanceled) {
		return Status.Canceled;
	}

	// The shipment was canceled -> Canceled
	/**
	 * trackingInformation.status=Canceled means the most recent OrderForm was canceled. That
	 * could be the OrderForm for a recollection, so we can't solely rely on it for the order-level
	 * status.
	 * Treat the full order as canceled if the tracking information is canceled and no req has been
	 * canceled, recollected, or accessioned.
	 */
	if (gearOrder.trackingInformation?.status === 'CANCELED') {
		const allReqsCanBeCanceled = gearOrder.requisitions?.every((gearReq) => canCancelTest({ gearOrder, gearReq }));
		if (allReqsCanBeCanceled) {
			return Status.Canceled;
		}
	}

	// At least one req has been discarded
	if (hasDiscardedSpecimens) {
		return Status.DiscardedSpecimens;
	}

	// All reqs have an accessionNo but not all orders' testing is complete -> ReceivedByLab
	if (allReqsHaveAccessionNo && !allOrdersReleased) {
		return Status.ReceivedByLab;
	}

	// All reqs have an accessionNo and all orders' testing is complete -> ResultsReady
	if (allReqsHaveAccessionNo && allOrdersReleased) {
		return Status.ResultsReady;
	}

	// Order has not been confirmed -> Incomplete
	if (!gearOrder.dataConfirmed) {
		return Status.Incomplete;
	}

	// A recollection has been started but not confirmed
	if (recollectionPending) {
		return Status.RecollectionPending;
	}

	// A pack on the order has been recollected and the recollection has no tracking details
	if (!hasTrackingNumber && recollectionCount > 0) {
		return Status.RecollectionOrdered;
	}

	// Order has no tracking details -> OrderPlaced
	if (!hasTrackingNumber) {
		return Status.OrderPlaced;
	}

	// All steps are complete (Including Specimen Collection) and we don't have an accessionNo -> ActivationComplete
	// This is before the deliveryDate check in case the user gets the pack and finishes activation before we have updated the order's delivery date
	if (allSamplesCollected && !allReqsHaveAccessionNo && gearOrder.workflow?.complete) {
		return Status.ActivationComplete;
	}

	// Order has a tracking number but no delivery date -> Shipped
	if (hasTrackingNumber && !gearOrder.trackingInformation.deliveryDate) {
		return Status.Shipped;
	}

	// Specimen Collection has not been provided -> SpecimenMissing
	if (!allSamplesCollected) {
		return Status.SpecimenMissing;
	}

	// Default to 'Incomplete'
	return Status.Incomplete;
};

export default PITLSummary;
